
import { defineComponent, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import KTAside from '@/layout/aside/Aside.vue';
import KTHeader from '@/layout/header/Header.vue';
import KTFooter from '@/layout/footer/Footer.vue';
import HtmlClass from '@/core/services/LayoutService';
import KTScrollTop from '@/layout/extras/ScrollTop.vue';
import KTUserMenu from '@/layout/header/partials/ActivityDrawer.vue';
import KTLoader from '@/components/Loader.vue';
import { Actions } from '@/store/enums/StoreEnums';
import { MenuComponent } from '@/assets/ts/components';
import { removeModalBackdrop } from '@/core/helpers/dom';
import { reinitializeComponents } from '@/core/plugins/keenthemes';
import {
  loaderEnabled,
  loaderLogo,
  asideEnabled,
  themeLightLogo,
  themeDarkLogo,
} from '@/core/helpers/config';

export default defineComponent({
  name: 'Layout',
  components: {
    KTAside,
    KTHeader,
    KTFooter,
    KTScrollTop,
    KTUserMenu,
    KTLoader,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading');
    HtmlClass.init();

    const pageTitle = computed(() => store.getters.pageTitle);

    const hideBackButton = computed(() => route.name === 'dashboard');

    const isFullscreen = computed(() => !!route.meta.fullscreen);

    const isDashboardRoute = computed(() => route.name === 'dashboard');

    // Old Metronic toolbar is gone — always use compact content spacing
    // (subheader padding removed) for non-fullscreen pages.
    const isCompactLayout = computed(() => !isFullscreen.value);

    const contentContainerClass = computed(() => {
      if (isFullscreen.value) {
        return 'p-0 h-100';
      }
      if (isDashboardRoute.value) {
        return 'px-2 vtu-dashboard-container';
      }
      return 'px-2';
    });

    watch(
      isCompactLayout,
      (compact) => {
        if (compact) {
          store.dispatch(Actions.ADD_BODY_CLASSNAME, 'toolbar-suppressed');
        } else {
          store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'toolbar-suppressed');
        }
      },
      { immediate: true },
    );

    watch(
      isFullscreen,
      (fullscreen) => {
        if (fullscreen) {
          store.dispatch(Actions.ADD_BODY_CLASSNAME, 'layout-fullscreen');
        } else {
          store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'layout-fullscreen');
        }
      },
      { immediate: true },
    );

    onMounted(() => {
      if (!store.getters.isUserAuthenticated) {
        router.push({ name: 'sign-in' });
      }

      nextTick(() => {
        reinitializeComponents();
      });

      setTimeout(() => {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading');
      }, 500);
    });

    // Overflow dropdowns are appended to document.body; restore/hide them
    // before Layout tears down (e.g. logout → sign-in).
    onBeforeUnmount(() => {
      MenuComponent.hideDropdowns(undefined);
    });

    watch(
      () => route.path,
      () => {
        MenuComponent.hideDropdowns(undefined);

        if (!store.getters.isUserAuthenticated) {
          router.push({ name: 'sign-in' });
        }

        nextTick(() => {
          reinitializeComponents();
        });
        removeModalBackdrop();
      },
    );

    return {
      loaderEnabled,
      loaderLogo,
      asideEnabled,
      isFullscreen,
      isDashboardRoute,
      contentContainerClass,
      pageTitle,
      themeLightLogo,
      themeDarkLogo,
      hideBackButton,
    };
  },
});
